import React from 'react';
import loadable from '@loadable/component';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';

import HeroSection from './sections/HeroSection';
import QuickSearchSection from './sections/QuickSearchSection';
import HowItWorksSection from './sections/HowItWorksSection';
import CampScoutSection from './sections/CampScoutSection';
import StatsSection from './sections/StatsSection';
import TrustSection from './sections/TrustSection';
import ScheduleSection from './sections/ScheduleSection';
import TestimonialsSection from './sections/TestimonialsSection';
import FAQSection from './sections/FAQSection';
import CTASection from './sections/CTASection';

const PageBuilderStyled = loadable(() =>
  import(/* webpackChunkName: "PageBuilderStyled" */ '../PageBuilder/PageBuilderStyled')
);

// Section name → custom sectionType mapping
const SECTION_MAP = [
  'hero',
  'quick-search',
  'how-it-works',
  'campscout',
  'stats',
  'trust',
  'schedule',
  'testimonials',
  'faq',
  'cta',
];

const SECTION_COMPONENTS = {
  hero: { component: HeroSection },
  'quick-search': { component: QuickSearchSection },
  'how-it-works': { component: HowItWorksSection },
  campscout: { component: CampScoutSection },
  stats: { component: StatsSection },
  trust: { component: TrustSection },
  schedule: { component: ScheduleSection },
  testimonials: { component: TestimonialsSection },
  faq: { component: FAQSection },
  cta: { component: CTASection },
};

export const LandingPageStyledComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  const pageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const sections = pageData?.sections;

  // Build custom sections array following bookyrslf pattern:
  // Filter sections by sectionName, wrap with custom sectionType.
  // Always emit all section slots — components use fallback content when Console data is missing.
  const customSections = SECTION_MAP.map(name => {
    const filtered = sections ? sections.filter(s => s.sectionName === name) : [];
    return {
      sectionId: name,
      sectionType: name,
      sectionData: filtered.length > 0 ? filtered : [],
    };
  });

  return (
    <PageBuilderStyled
      pageAssetsData={{
        ...pageData,
        sections: customSections,
      }}
      options={{
        sectionComponents: SECTION_COMPONENTS,
      }}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
    />
  );
};

LandingPageStyledComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

const LandingPageStyled = compose(connect(mapStateToProps))(LandingPageStyledComponent);

export default LandingPageStyled;
